import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, history } from 'lib';
import Home from 'pages/Home';
import { AuthEntity } from '_entities';
import { URLS } from '_constants';
import { ToastContainer } from 'react-toastify';
import { QueryParamProvider } from 'use-query-params';
import { useGoogleMaps, useNewUi } from 'hooks';
import PrivateRoute from './modules/PrivateRoute';
import PublicOnlyRoute from './modules/PublicOnlyRoute';
import VersionWatcher from './modules/VersionWatcher';
import DraftReminder from './modules/DraftReminder';
import { tokenService, offlineModeService } from './utils/services';
import Loader from './ui-kit/Loader';
import 'assets/styles/reset.scss';
import 'assets/styles/fonts.scss';
import 'react-toastify/dist/ReactToastify.min.css';
import 'assets/styles/react-toastify.scss';
import 'assets/styles/base.scss';
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from './routes';

const { getAppCanStart, getPermissionStatus } = AuthEntity.selectors;
const { getModularityPermissions } = AuthEntity.actions;

function App() {
  const [isAppLoading, setIsAppLoading] = useState(false);
  const dispatch = useDispatch();
  const isAppCanStart = useSelector(getAppCanStart);
  const isPermissionReady = useSelector(getPermissionStatus);
  const newUi = useNewUi();

  useGoogleMaps();

  // New design (new_ui_2026_06) is delivered as a single attribute on <html>: ui-kit reads it via
  // the CSS custom properties in assets/styles/base.scss, so no component has to subscribe to the
  // flag itself (a table with 200 action cells would otherwise pay for 200 selector subscriptions).
  // Layout effect, so the tokens are in place before the browser paints the routes below.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (newUi) root.dataset.newUi = 'true';
    else delete root.dataset.newUi;
  }, [newUi]);

  useEffect(() => {
    (async () => {
      try {
        setIsAppLoading(true);
        await tokenService.startApp();
      } catch (e) {
        tokenService.removeToken();
      } finally {
        setIsAppLoading(false);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getModularityPermissions());
  }, [dispatch]);

  useEffect(() => {
    offlineModeService.syncToServiceWorker();
  }, []);

  const isBootstrapping = isAppLoading || !isAppCanStart || !isPermissionReady;

  return (
    <>
      {isBootstrapping ? (
        <Loader coverElement />
      ) : (
        <Router history={history}>
          <QueryParamProvider ReactRouterRoute={Route}>
            <Switch>
              <Route exact path="/" component={Home} />
              {PRIVATE_ROUTES.map((route) => (
                <PrivateRoute
                  key={route.path}
                  exact={route.exact}
                  path={route.path}
                  component={route.component}
                  {...route}
                />
              ))}
              {PUBLIC_ROUTES.map((route) => {
                // Auth pages (login, forgot password) bounce a signed-in user to the dashboard; every
                // other public page stays reachable with a session. See modules/PublicOnlyRoute.
                const RouteComponent = route.redirectIfAuthenticated ? PublicOnlyRoute : Route;
                return (
                  <RouteComponent
                    key={route.path}
                    exact={route.exact}
                    path={route.path}
                    component={route.component}
                    {...route}
                  />
                );
              })}
              <Redirect to={URLS.dashboard} />
            </Switch>
          </QueryParamProvider>
        </Router>
      )}
      <ToastContainer newestOnTop limit={5} />
      <VersionWatcher />
      <DraftReminder />
    </>
  );
}

const AppWrapper = () => {
  return (
    <Provider store={store}>
      <App />
    </Provider>
  );
};

export default AppWrapper;
